import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    fetchMembershipEnrollmentsThunk,
    MembershipsStatus,
    retryPaymentThunk,
    searchEnrollmentsThunk,
    fetchMarketplaceEnrollmentsThunk,
    getEnrollmentThunk,
    retryMultiplePaymentsThunk
} from '@spike/memberships-action';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import EnrollmentListView from 'components/Memberships/Detail/EnrollmentListView';
import { Enrollment } from '@spike/membership-model';
import useMembershipDetailsStyles from 'components/Memberships/Detail/MembershipDetailsStyles';
import EnrollmentDetails from 'components/Memberships/Enrollment/EnrollmentDetails';
import UpdateEnrollmentCardDrawer from 'components/Memberships/Enrollment/UpdateEnrollmentCardDrawer';
import SellMembership from 'components/Memberships/Sell/SellMembership';
import { useNonInitialEffect } from '@versiondos/hooks';
import { EnrollmentsPagination, useEnrollmentFilters } from './hooks/useEnrollmentFilters';
import type { TableColumn } from 'components/Memberships/Detail/MembershipDetailsTable';
import { useDebounce } from 'hooks/useDebounce';
import { usePermissions } from 'hooks/usePermissions';
import EnrollmentUsageFilters, { EnrollmentUsageSelection } from './EnrollmentUsageFilters';
import EnrollmentsExportCSV from './EnrollmentsExportCSV';
import styles from './SoldMembershipsContainer.module.scss';

const columnsForSoldMemberships: TableColumn[] = [
    'membership',
    'client',
    'pet',
    'type',
    'startDate',
    'endDate',
    'daysLeft',
    'nextBilling',
    'status',
    'total',
    'actions'
];

const SoldMembershipsContainer: React.FC = () => {
    const classes = useMembershipDetailsStyles();
    const permissions = usePermissions();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const location = useLocation();
    const history = useHistory();
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 400);
    const [selectedOption, setSelectedOption] = useState('active');
    const [usageFilters, setUsageFilters] = useState<EnrollmentUsageSelection>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [showEnrollmentDetails, setShowEnrollmentDetails] = useState(false);
    const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<number | null>(null);
    const [showClientCards, setShowClientCards] = useState(false);
    const [localEnrollment, setLocalEnrollment] = useState<Enrollment | null>(null);
    const enrollmentState = useSelector<RootState, Enrollment | null>(state => state.memberships.enrollmentSelected);
    const [loadingCards, setLoadingCards] = useState(false);
    const [showSellMembership, setShowSellMembership] = useState(false);
    const [loading, setLoading] = useState(true);
    const enrollments = useSelector<RootState, Enrollment[]>(state => state.memberships.enrollments);
    const enrollmentsPagination = useSelector<RootState, EnrollmentsPagination | null>(
        state => state.memberships.enrollmentsPagination
    );
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);

    const membershipId = undefined;

    const [lastFetchedStatus, setLastFetchedStatus] = useState(selectedOption);

    const { getFilterOptions, getAlerts } = useEnrollmentFilters({
        loading,
        lastFetchedStatus,
        enrollmentsPagination
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const enrollmentId = params.get('enrollmentId');
        if (enrollmentId) {
            setSelectedEnrollmentId(Number(enrollmentId));
            setShowEnrollmentDetails(true);
        }
    }, [location.search]);

    useEffect(() => {
        fetchEnrollments();
    }, [currentPage, selectedOption, pageSize, debouncedSearchText, usageFilters]);

    useEffect(() => {
        setLoadingCards(false);
    }, [localEnrollment]);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.GetEnrollmentsSuccess:
                setLoading(false);
                break;
            case MembershipsStatus.GettingEnrollments:
                setLoading(true);
                break;
            case MembershipsStatus.GetEnrollmentsError:
                setLoading(false);
                break;
            case MembershipsStatus.CancelEnrollmentSuccess:
                fetchEnrollments();
                break;
            case MembershipsStatus.RetryPaymentSuccess:
                setLoading(true);
                refreshEnrollments();
                break;
            case MembershipsStatus.RetryMultiplePaymentsSuccess:
                setLoading(true);
                refreshEnrollments();
                break;
            case MembershipsStatus.fetchMarketplaceEnrollmentsSuccess:
                setLoading(false);
                break;
            case MembershipsStatus.fetchMarketplaceEnrollmentsError:
                setLoading(false);
                break;
            case MembershipsStatus.FetchingMarketplaceEnrollments:
                setLoading(true);
                break;
            case MembershipsStatus.SearchEnrollmentsSuccess:
                setLoading(false);
                break;
            case MembershipsStatus.SearchEnrollmentsError:
                setLoading(false);
                break;
            case MembershipsStatus.SearchingEnrollments:
                setLoading(true);
                break;
            case MembershipsStatus.GetEnrollmentSuccess:
                setLocalEnrollment(enrollmentState);
                break;
            case MembershipsStatus.GetEnrollmentError:
                setLoadingCards(false);
                break;
            case MembershipsStatus.GettingEnrollment:
                setLoadingCards(true);
                break;
        }
    }, [membershipsStatus]);

    const fetchEnrollments = () => {
        if (debouncedSearchText.length >= 3) {
            dispatch(
                searchEnrollmentsThunk(
                    apiClientWrapper,
                    debouncedSearchText,
                    membershipId,
                    currentPage,
                    pageSize,
                    selectedOption === 'all' ? undefined : selectedOption,
                    undefined,
                    usageFilters
                )
            );
        } else if (debouncedSearchText.length === 0) {
            dispatch(
                fetchMarketplaceEnrollmentsThunk(
                    apiClientWrapper,
                    currentPage,
                    selectedOption === 'all' ? undefined : selectedOption,
                    pageSize,
                    usageFilters
                )
            );
        }
    };

    const getEnrollment = (enrollment: Enrollment) => {
        setLoadingCards(true);
        dispatch(getEnrollmentThunk(apiClientWrapper, enrollment.id));
    };

    const handleStatusFilter = (option: string) => {
        setSelectedOption(option);
        setCurrentPage(1);
        setLastFetchedStatus(option);
    };

    const handleUsageFiltersChange = (value: EnrollmentUsageSelection) => {
        setUsageFilters(value);
        setCurrentPage(1);
    };

    const handleSearchTextChange = (value: string) => {
        setSearchText(value);
        setCurrentPage(1);
    };

    const handleEnrollmentClick = (enrollment: Enrollment) => {
        setSelectedEnrollmentId(enrollment.id);
        setShowEnrollmentDetails(true);
    };

    const handleUpdateEnrollment = (enrollment: Enrollment) => {
        setLoadingCards(true);
        getEnrollment(enrollment);
        setShowClientCards(true);
    };

    const handleRetryPayment = (enrollment: Enrollment) => {
        let enrollmentIds = enrollment.pastDueEnrollments
            ? enrollment.pastDueEnrollments.map(e => e.id).join(',')
            : enrollment.id.toString();
        if (enrollment.pastDueEnrollments && enrollment.pastDueEnrollments.length > 0) {
            enrollmentIds = enrollmentIds + ',' + enrollment.id;
        }
        //dispatch(retryPaymentThunk(apiClientWrapper, enrollment.id));
        dispatch(retryMultiplePaymentsThunk(apiClientWrapper, enrollmentIds));
    };

    const handleCloseEnrollmentDetails = () => {
        setShowEnrollmentDetails(false);
        const params = new URLSearchParams(location.search);
        if (params.has('enrollmentId')) {
            params.delete('enrollmentId');
            history.replace({ search: params.toString(), hash: location.hash });
        }
    };

    const handleCloseClientCards = () => {
        setShowClientCards(false);
    };

    const handleSellMembership = () => setShowSellMembership(true);
    const handleCloseSellMembership = () => setShowSellMembership(false);

    const refreshEnrollments = () => {
        setTimeout(() => {
            fetchEnrollments();
        }, 3000);
    };

    const onPaid = () => {
        setLoading(true);
        refreshEnrollments();
    };

    const sellMembership = (
        <SellMembership
            onClose={handleCloseSellMembership}
            onPaid={onPaid}
        />
    );

    const enrollmentDetails = () =>
        selectedEnrollmentId && (
            <EnrollmentDetails
                enrollmentId={selectedEnrollmentId}
                onClose={handleCloseEnrollmentDetails}
            />
        );

    const onFinishUpdate = (enrollment: Enrollment | null) => {
        if (!enrollment) {
            return;
        }

        if (enrollment.status != 'past_due') {
            getEnrollment(enrollment);
        }
    };

    const clientCards = () =>
        showClientCards && (
            <UpdateEnrollmentCardDrawer
                onClose={handleCloseClientCards}
                enrollment={localEnrollment}
                loading={loadingCards}
                onFinishUpdate={() => onFinishUpdate(localEnrollment)}
            />
        );

    const tableProps = {
        loading,
        enrollments,
        pagination: enrollmentsPagination,
        onPageChange: setCurrentPage,
        pageSize,
        onPageSizeChange: setPageSize,
        filterSelectedOption: selectedOption,
        onEnrollmentClick: handleEnrollmentClick,
        onUpdateEnrollment: handleUpdateEnrollment,
        onRetryPayment: handleRetryPayment,
        onPaid: refreshEnrollments,
        columns: columnsForSoldMemberships
    };

    const alerts = getAlerts(classes);

    const headerRightContent = () => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {permissions.memberships.sell && (
                <Button
                    leftIcon={faPlus}
                    label="Sell Membership"
                    onClick={handleSellMembership}
                    id="sold_memberships_sell_button"
                />
            )}
        </div>
    );

    const searchActions = (
        <div className={styles.searchActions}>
            <EnrollmentUsageFilters
                value={usageFilters}
                onChange={handleUsageFiltersChange}
            />
            <EnrollmentsExportCSV
                status={selectedOption === 'all' ? undefined : selectedOption}
                usageFilters={usageFilters}
            />
        </div>
    );

    return (
        <>
            <EnrollmentListView
                title="Sold Memberships"
                customButton={headerRightContent()}
                filters={{
                    options: getFilterOptions(),
                    selectedId: selectedOption,
                    onSelect: handleStatusFilter,
                    className: classes.SelectionBar
                }}
                search={{
                    value: searchText,
                    setValue: handleSearchTextChange,
                    className: classes.searchBarContainer,
                    placeholder: 'Search by Client or Pet',
                    rightActions: searchActions
                }}
                tableProps={tableProps}
                classes={{
                    wrapperContainer: classes.wrapperContainer,
                    headWrapper: classes.headWrapper,
                    alertsContainer: classes.alertsContainer,
                    content: classes.content,
                    headerContainer: classes.headerContainerCentered
                }}
                alerts={alerts}
                emptyDescription="Once someone purchases a membership, they'll appear here."
            />
            {showEnrollmentDetails && enrollmentDetails()}
            {showClientCards && clientCards()}
            {showSellMembership && sellMembership}
        </>
    );
};

export default SoldMembershipsContainer;
